import {
  DomainAddressType,
  type DtoAddress,
  type DtoCreateAddressReq,
  type DtoGetBorrowersAddressesResp,
  type DtoUpdateAddressReq,
  type DomainHousingType,
} from "@porchpass/api-client";
import { DLA_ADDRESS_HOUSING_OTHER } from "src/forms/validation/conditionalOtherConfigs";
import { otherTextForApi } from "src/forms/validation/conditionalOther";
import { createIsoDate, parseIsoDate, safeNumber } from "src/utils";
import { requiredField } from "./required";

export type CurrentAddressFormData = {
  street1?: string;
  street2?: string;
  city?: string;
  county?: string;
  state?: string;
  zip_code?: string;
  housing_type?: DomainHousingType;
  housing_type_other?: string;
  monthly_payment?: string;
  from_month?: string;
  from_year?: string;
  to_month?: string;
  to_year?: string;
  is_mailing_address?: boolean;
  has_valid_current_address?: boolean;
};

export type PreviousAddressFormData = CurrentAddressFormData & {
  to_month?: string;
  to_year?: string;
};

export type MailingAddressFormData = Pick<
  CurrentAddressFormData,
  "street1" | "city" | "state" | "zip_code"
>;

export type AddressListItemFormData = Omit<DtoAddress, "monthly_payment"> & {
  monthly_payment?: string;
  from_month?: string;
  from_year?: string;
  to_month?: string;
  to_year?: string;
};

export type AddressesListFormData = {
  addresses: AddressListItemFormData[];
  has_sufficient_address_history?: boolean;
};

function housingTypeOther(data: CurrentAddressFormData): string | undefined {
  return otherTextForApi(
    data.housing_type,
    data.housing_type_other,
    DLA_ADDRESS_HOUSING_OTHER,
  );
}

export function toCreateCurrentAddressReq(
  data: CurrentAddressFormData,
): DtoCreateAddressReq {
  return {
    address_type: DomainAddressType.AddressCurrent,
    street1: data.street1 ?? "",
    street2: data.street2 ?? "",
    city: data.city ?? "",
    county: data.county ?? "",
    state: data.state ?? "",
    zip_code: data.zip_code ?? "",
    housing_type_other: housingTypeOther(data),
    housing_type: data.housing_type,
    monthly_payment: safeNumber(data.monthly_payment),
    resided_from: createIsoDate(
      safeNumber(data.from_year),
      safeNumber(data.from_month),
    ),
    resided_to: undefined,
    is_mailing_address: data?.is_mailing_address,
  };
}

export function toUpdateCurrentAddressReq(
  data: CurrentAddressFormData,
): DtoUpdateAddressReq {
  return toCreateCurrentAddressReq(data);
}

export function toCreatePreviousAddressReq(
  data: PreviousAddressFormData,
): DtoCreateAddressReq {
  return {
    address_type: DomainAddressType.AddressPrevious,
    street1: data.street1 ?? "",
    city: data.city ?? "",
    state: data.state ?? "",
    zip_code: data.zip_code ?? "",
    resided_from: createIsoDate(
      safeNumber(data.from_year),
      safeNumber(data.from_month),
    ),
    resided_to: createIsoDate(
      safeNumber(data.to_year),
      safeNumber(data.to_month),
    ),
  };
}

export function toUpdatePreviousAddressReq(
  data: PreviousAddressFormData,
): DtoUpdateAddressReq {
  return {
    street1: data.street1 ?? "",
    city: data.city ?? "",
    state: data.state ?? "",
    zip_code: data.zip_code ?? "",
    resided_from: createIsoDate(
      safeNumber(data.from_year),
      safeNumber(data.from_month),
    ),
    resided_to: createIsoDate(
      safeNumber(data.to_year),
      safeNumber(data.to_month),
    ),
  };
}

export function toCreateMailingAddressReq(
  data: MailingAddressFormData,
): DtoCreateAddressReq {
  return {
    address_type: DomainAddressType.AddressMailing,
    street1: data.street1 ?? "",
    city: data.city ?? "",
    state: data.state ?? "",
    zip_code: data.zip_code ?? "",
  };
}

export function toUpdateMailingAddressReq(
  data: MailingAddressFormData,
): DtoUpdateAddressReq {
  return {
    street1: data?.street1 ?? "",
    city: data?.city ?? "",
    state: data?.state ?? "",
    zip_code: data?.zip_code ?? "",
  };
}

export function fromAddressesList(
  data: DtoGetBorrowersAddressesResp,
): AddressesListFormData {
  const mappedAddresses = (data.addresses ?? []).map((address) => {
    const { monthly_payment, ...rest } = address;
    const mappedData: AddressListItemFormData = {
      ...rest,
      monthly_payment: monthly_payment?.toString(),
    };

    if (address?.resided_from) {
      const { month: from_month = "0", year: from_year = "0" } = parseIsoDate(
        address.resided_from,
      );
      mappedData.from_year = from_year;
      mappedData.from_month = from_month;
    }

    if (address?.resided_to) {
      const { month: to_month = "0", year: to_year = "0" } = parseIsoDate(
        address.resided_to,
      );
      mappedData.to_year = to_year;
      mappedData.to_month = to_month;
    }

    return mappedData;
  });

  return {
    addresses: mappedAddresses,
    has_sufficient_address_history: data.has_sufficient_address_history,
  };
}

export function toCoborrowerConfirmationAddressReq(
  data: CurrentAddressFormData,
): DtoCreateAddressReq {
  return {
    address_type: DomainAddressType.AddressCurrent,
    street1: data.street1 ?? "",
    street2: data.street2 ?? "",
    city: data.city ?? "",
    county: data.county ?? "",
    state: data.state ?? "",
    zip_code: data.zip_code ?? "",
    housing_type_other: housingTypeOther(data),
    housing_type: data.housing_type,
    monthly_payment: safeNumber(data.monthly_payment),
    resided_from: createIsoDate(
      safeNumber(data.from_year),
      safeNumber(data.from_month),
    ),
    resided_to: undefined,
    is_mailing_address: false,
  };
}

export function fromCoborrowerCurrentAddress(
  data: DtoAddress & { message?: string },
): CurrentAddressFormData {
  if ("message" in data) {
    return { has_valid_current_address: false };
  }
  const { id: _id, monthly_payment, ...rest } = data;
  const mappedData: CurrentAddressFormData = {
    ...rest,
    monthly_payment: monthly_payment?.toString(),
  };
  const { month: from_month = "0", year: from_year = "0" } = parseIsoDate(
    requiredField(data.resided_from, "resided_from"),
  );
  mappedData.from_year = from_year;
  mappedData.from_month = from_month;

  if (data?.resided_to) {
    const { month: to_month = "0", year: to_year = "0" } = parseIsoDate(
      data.resided_to,
    );
    mappedData.to_year = to_year;
    mappedData.to_month = to_month;
  }
  mappedData.has_valid_current_address = Boolean(
    data.street1 != null ||
      data.city != null ||
      data.state != null ||
      data.zip_code != null,
  );
  return mappedData;
}
